import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '../../lib/cn';
import { errorMessage } from '../../lib/api-client';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss; `0` keeps it until dismissed. */
  duration?: number;
}

interface ToastApi {
  show: (input: ToastInput) => void;
  success: (title: string, description?: string) => void;
  /** For outcomes that are neither a failure nor a clean success. */
  info: (title: string, description?: string) => void;
  error: (title: string, error?: unknown) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONES: Record<ToastTone, string> = {
  success: 'border-positive/40 bg-positive/10 text-positive',
  error: 'border-negative/40 bg-negative/10 text-negative',
  info: 'border-accent/40 bg-accent/10 text-accent',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    ({ tone = 'info', title, description, duration = 5000 }: ToastInput) => {
      const id = nextId.current;
      nextId.current += 1;

      setToasts((current) => [
        ...current,
        { id, tone, title, ...(description === undefined ? {} : { description }) },
      ]);

      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) =>
        show({ tone: 'success', title, ...(description === undefined ? {} : { description }) }),
      info: (title, description) =>
        show({ tone: 'info', title, ...(description === undefined ? {} : { description }) }),
      // Accepts the raw rejection so call sites never format API errors by hand.
      error: (title, error) =>
        show({
          tone: 'error',
          title,
          ...(error === undefined ? {} : { description: errorMessage(error) }),
        }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // Status changes are announced without stealing focus (TZ §39 a11y).
        role="status"
        aria-live="polite"
        className="safe-bottom pointer-events-none fixed inset-x-0 bottom-0 z-60 flex flex-col items-center gap-2 px-4 sm:bottom-4 sm:items-end"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto w-full max-w-sm animate-sheet-in rounded-control border',
              'bg-surface px-4 py-3 shadow-card backdrop-blur',
              TONES[toast.tone],
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">{toast.title}</p>
                {toast.description ? (
                  <p className="mt-0.5 text-sm text-ink-muted">{toast.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Скрыть уведомление"
                className="-my-1 -mr-1 grid size-9 shrink-0 place-items-center rounded-control text-ink-muted hover:text-ink"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
